// src/app/pages/admin/AdminEventoEmpleabilidadFormWrapper.tsx
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import { useAuth } from '../../modules/auth';
import {
	createEventoEmpleabilidad,
	updateEventoEmpleabilidad,
	getEventoEmpleabilidadById,
	getLocalidades,
	getEstadosEventoEmpleabilidad,
	getTiposEventoEmpleabilidad,
	getTiposPoblacionPriorizadaEventoEmpleabilidad,
	getFranjasHorariasEventoEmpleabilidad,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
} from '@mui/material';
import { AsociacionVacantesPage } from './AdminEventoAsociacionVacantesWrapper';
import { AsociacionOrientacionesPage } from './AdminEventoAsociacionOrientacionesWrapper';
import { AsociacionFormacionesPage } from './AdminEventoAsociacionFormacionesWrapper';
import {
	Localidad,
	ParametroEventoEmpleabilidad,
	esEventoPoblacional,
	EMPTY_FORM,
	FormState,
	buildPayload,
	eventoToForm,
	getTodayISO,
	getErrorMessage,
} from './EventosEmpleabilidad.helpers';

interface AdminEventoEmpleabilidadFormWrapperProps {
	mode: 'create' | 'edit' | 'view';
}

const EventoEmpleabilidadFormPage = ({ mode }: AdminEventoEmpleabilidadFormWrapperProps) => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { isAdminRole, isCandidateRole, isEmployerRole } = useAuth();
	const viewOnly = mode === 'view';
	const editingId = mode !== 'create' && id ? Number(id) : null;

	const EVENTOS_LIST_PATH = isAdminRole()
		? '/admin/gestion-eventos'
		: isCandidateRole()
		? '/candidato/gestion-eventos'
		: isEmployerRole()
		? '/empleador/gestion-eventos'
		: '/consejero/gestion-eventos';

	const [localidades, setLocalidades] = useState<Localidad[]>([]);
	const [estados, setEstados] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [tiposEvento, setTiposEvento] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [tiposPoblacionPriorizada, setTiposPoblacionPriorizada] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [franjasHorarias, setFranjasHorarias] = useState<ParametroEventoEmpleabilidad[]>([]);

	const [loading, setLoading] = useState(false);
	const [isSubmitting, setSubmitting] = useState(false);
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

	const [open, setOpen] = useState(false);
	const [alert, setAlert] = useState(true);
	const [alertMsg, setAlertMsg] = useState('');

	const notify = (msg: string, success = true) => {
		setAlert(success);
		setAlertMsg(msg);
		setOpen(true);
		setTimeout(() => setOpen(false), 3000);
	};

	useEffect(() => {
		getLocalidades()
			.then((response: any) => {
				if (response && response.data) setLocalidades(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getEstadosEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setEstados(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getTiposEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setTiposEvento(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getTiposPoblacionPriorizadaEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setTiposPoblacionPriorizada(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getFranjasHorariasEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setFranjasHorarias(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		if (mode === 'create') {
			return;
		}
		if (!editingId) {
			navigate(EVENTOS_LIST_PATH);
			return;
		}
		setLoading(true);
		getEventoEmpleabilidadById(editingId)
			.then((response: any) => {
				const evento = response?.data || response;
				if (evento) setForm(eventoToForm(evento));
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, editingId]);

	useEffect(() => {
		if (mode !== 'create' || estados.length === 0) return;
		const estadoCreado = estados.find((e) => e.code === '1') || estados[0];
		setForm((prev) => ({ ...prev, estado: estadoCreado ? String(estadoCreado.id) : '' }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, estados]);

	const getFieldErrors = (): Partial<Record<keyof FormState, string>> => {
		const errors: Partial<Record<keyof FormState, string>> = {};

		if (!form.nombre_evento.trim()) errors.nombre_evento = 'El nombre del evento es obligatorio.';
		else if (form.nombre_evento.trim().length > 200) errors.nombre_evento = 'El nombre del evento no puede superar 200 caracteres.';

		if (!form.tipo_evento) {
			errors.tipo_evento =
				tiposEvento.length === 0
					? 'No hay tipos de evento disponibles (no se pudo cargar el parámetro TIPO_EVENTO).'
					: 'Debe seleccionar el tipo de evento.';
		} 
		if (esEventoPoblacional(form.tipo_evento) && form.tipo_poblacion_priorizada.length === 0) {
			errors.tipo_poblacion_priorizada =
				tiposPoblacionPriorizada.length === 0
					? 'No hay poblaciones priorizadas disponibles (no se pudo cargar el parámetro TIPO_POBLACION_PRIORIZADA).'
					: 'Debe seleccionar la población priorizada.';
		}

		if (!form.fecha_inicio_evento) errors.fecha_inicio_evento = 'Debe indicar la fecha de inicio del evento.';
		else if (form.fecha_inicio_evento < getTodayISO()) {
			errors.fecha_inicio_evento = 'La fecha de inicio del evento no puede ser anterior a la fecha actual.';
		}

		if (!form.fecha_fin_evento) errors.fecha_fin_evento = 'Debe indicar la fecha final del evento.';

		if (form.fecha_inicio_evento && form.fecha_fin_evento && form.fecha_fin_evento < form.fecha_inicio_evento) {
			const mensaje = 'La fecha final del evento no puede ser anterior a la fecha de inicio.';
			errors.fecha_fin_evento = mensaje;
			errors.fecha_inicio_evento = errors.fecha_inicio_evento || mensaje;
		}

		if (!form.localidad_id) errors.localidad_id = 'Debe seleccionar la localidad.';
		if (!form.estado) {
			errors.estado =
				estados.length === 0
					? 'No hay estados disponibles (no se pudo cargar el parámetro ESTADO).'
					: 'Debe seleccionar el estado del evento.';
		}

		if (!form.lugar.trim()) errors.lugar = 'El lugar es obligatorio.';
		else if (form.lugar.trim().length > 300) errors.lugar = 'El lugar no puede superar 300 caracteres.';

		if (form.direccion && form.direccion.trim().length > 300) errors.direccion = 'La dirección no puede superar 300 caracteres.';

		if (form.requiere_prerregistro === 'SI') {
			if (!form.fecha_inicio_prerregistro) errors.fecha_inicio_prerregistro = 'Debe indicar la fecha de inicio del prerregistro.';
			else if (form.fecha_inicio_prerregistro < getTodayISO()) {
				errors.fecha_inicio_prerregistro = 'La fecha de inicio del prerregistro no puede ser anterior a la fecha actual.';
			}
			if (!form.fecha_fin_prerregistro) errors.fecha_fin_prerregistro = 'Debe indicar la fecha final del prerregistro.';
			if (
				form.fecha_inicio_prerregistro &&
				form.fecha_fin_prerregistro &&
				form.fecha_fin_prerregistro < form.fecha_inicio_prerregistro
			) {
				errors.fecha_fin_prerregistro = 'La fecha final del prerregistro no puede ser anterior a la fecha de inicio.';
			}

			if (form.franjas.length === 0) {
				errors.franjas =
					franjasHorarias.length === 0
						? 'No hay franjas horarias disponibles (no se pudo cargar el parámetro FRANJA_HORARIA).'
						: 'Debe seleccionar al menos una franja horaria.';
			} else if (form.franjas.some((franja) => !franja.cupo_maximo_prerregistro || Number(franja.cupo_maximo_prerregistro) < 1)) {
				errors.franjas = 'El cupo máximo de prerregistro debe ser mayor o igual a 1 para cada franja horaria seleccionada.';
			}
			if (!form.requiere_notificacion_correo) errors.requiere_notificacion_correo = 'Debe indicar si se notifica por correo.';
		}

		return errors;
	};

	const fieldErrors = getFieldErrors();
	const isFormValid = () => Object.keys(fieldErrors).length === 0;
	const fieldError = (field: keyof FormState): string | undefined =>
		formSubmitAttempted ? fieldErrors[field] : undefined;

	const handleSubmitEvento = async () => {
		setFormSubmitAttempted(true);
		if (!isFormValid()) {
			if (fieldErrors.fecha_inicio_evento) notify(fieldErrors.fecha_inicio_evento, false);
			return;
		}
		try {
			setSubmitting(true);
			const payload = buildPayload(form, { isCreate: !editingId });
			let successMsg: string;
			if (editingId) {
				await updateEventoEmpleabilidad(editingId, payload);
				successMsg = 'Evento actualizado con éxito.';
			} else {
				await createEventoEmpleabilidad(payload);
				successMsg = 'Evento registrado con éxito.';
			}
			navigate(EVENTOS_LIST_PATH, { state: { notify: { msg: successMsg, success: true } } });
		} catch (error: any) {
			notify(getErrorMessage(error), false); 
		} finally {
			setSubmitting(false);
		}
	};

	const title = viewOnly ? 'Ver evento' : editingId ? 'Editar evento' : 'Registrar evento';

	return (
		<div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
			<LoadingBackdrop loading={loading || isSubmitting} />
			<div className='row mb-4' style={{ alignItems: 'center' }}>
				<div className='col-sm-12 d-flex align-items-center' style={{ gap: '15px' }}>
					<BackButton to={EVENTOS_LIST_PATH} />
					<h2 className='m-0'>{title}</h2>
				</div>
			</div>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						label='Código del evento'
						value={editingId ? form.codigo_evento : ''}
						disabled
						InputLabelProps={{ shrink: true }}
						helperText={
							editingId
								? 'Código asignado automáticamente, no editable.'
								: 'Se generará automáticamente al registrar el evento.'
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						label='Nombre del evento *'
						inputProps={{ maxLength: 200 }}
						value={form.nombre_evento}
						onChange={(e) => setForm({ ...form, nombre_evento: e.target.value })}
						error={!!fieldError('nombre_evento')}
						helperText={fieldError('nombre_evento')}
						disabled={viewOnly}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormControl fullWidth error={!!fieldError('tipo_evento')}>
						<InputLabel id='tipo-form-label' shrink>Tipo de evento *</InputLabel>
						<Select
							labelId='tipo-form-label'
							label='Tipo de evento *'
							displayEmpty
							notched
							disabled={viewOnly || !!editingId}
							value={form.tipo_evento}
							onChange={(e) => setForm({ ...form, tipo_evento: e.target.value })}
						>
							<MenuItem value=''>
								<em>Seleccione</em>
							</MenuItem>
							{tiposEvento.map((tipo) => (
								<MenuItem key={tipo.id} value={tipo.value}>
									{tipo.label || tipo.value}
								</MenuItem>
							))}
						</Select>
						{fieldError('tipo_evento') ? (
							<FormHelperText>{fieldError('tipo_evento')}</FormHelperText>
						) : (
							editingId && <FormHelperText>No editable después de creado el evento.</FormHelperText>
						)}
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormControl fullWidth error={!!fieldError('tipo_poblacion_priorizada')}>
						<InputLabel id='poblacion-form-label' shrink>
							{esEventoPoblacional(form.tipo_evento) ? 'Tipo de Población priorizada *' : 'Tipo de Población priorizada'}
						</InputLabel>
						<Select
							labelId='poblacion-form-label'
							label={esEventoPoblacional(form.tipo_evento) ? 'Población priorizada *' : 'Población priorizada'}
							multiple
							displayEmpty
							notched
							disabled={viewOnly}
							value={form.tipo_poblacion_priorizada}
							onChange={(e) => {
								const value = e.target.value;
								setForm({
									...form,
									tipo_poblacion_priorizada: typeof value === 'string' ? value.split(',') : value,
								});
							}}
							renderValue={(selected) =>
								selected.length === 0 ? (
									<em>Seleccione</em>
								) : (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										{selected.map((value) => {
											const tipo = tiposPoblacionPriorizada.find((t) => t.value === value);
											return <Chip key={value} label={tipo?.label || tipo?.value || value} size='small' />;
										})}
									</Box>
								)
							}
						>
							{tiposPoblacionPriorizada.map((tipo) => (
								<MenuItem key={tipo.id} value={tipo.value}>
									<Checkbox checked={form.tipo_poblacion_priorizada.includes(tipo.value)} />
									<ListItemText primary={tipo.label || tipo.value} />
								</MenuItem>
							))}
						</Select>
						{fieldError('tipo_poblacion_priorizada') && (
							<FormHelperText>{fieldError('tipo_poblacion_priorizada')}</FormHelperText>
						)}
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormControl fullWidth>
						<InputLabel id='dura-mas-form-label'>¿Dura más de un día?</InputLabel>
						<Select
							labelId='dura-mas-form-label'
							label='¿Dura más de un día?'
							disabled={viewOnly}
							value={form.dura_mas_de_un_dia}
							onChange={(e) => {
								const duraMasDeUnDia = e.target.value;
								setForm({
									...form,
									dura_mas_de_un_dia: duraMasDeUnDia,
									fecha_fin_evento: duraMasDeUnDia === 'NO' ? form.fecha_inicio_evento : form.fecha_fin_evento,
								});
							}}
						>
							<MenuItem value='SI'>Sí</MenuItem>
							<MenuItem value='NO'>No</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						type='date'
						label='Fecha inicio evento *'
						InputLabelProps={{ shrink: true }}
						inputProps={{ min: getTodayISO() }}
						disabled={viewOnly}
						value={form.fecha_inicio_evento}
						onChange={(e) => {
							const fechaInicio = e.target.value;
							setForm({
								...form,
								fecha_inicio_evento: fechaInicio,
								fecha_fin_evento: form.dura_mas_de_un_dia === 'NO' ? fechaInicio : form.fecha_fin_evento,
							});
						}}
						error={!!fieldError('fecha_inicio_evento')}
						helperText={fieldError('fecha_inicio_evento')}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						type='date'
						label='Fecha final evento *'
						InputLabelProps={{ shrink: true }}
						disabled={viewOnly || form.dura_mas_de_un_dia === 'NO'}
						value={form.fecha_fin_evento}
						onChange={(e) => setForm({ ...form, fecha_fin_evento: e.target.value })}
						error={!!fieldError('fecha_fin_evento')}
						helperText={fieldError('fecha_fin_evento')}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormControl fullWidth error={!!fieldError('localidad_id')}>
						<InputLabel id='localidad-form-label' shrink>Localidad *</InputLabel>
						<Select
							labelId='localidad-form-label'
							label='Localidad *'
							displayEmpty
							notched
							disabled={viewOnly}
							value={form.localidad_id}
							onChange={(e) => setForm({ ...form, localidad_id: e.target.value })}
						>
							<MenuItem value=''>
								<em>Seleccione</em>
							</MenuItem>
							{localidades.map((localidad) => (
								<MenuItem key={localidad.id_localidad} value={String(localidad.id_localidad)}>
									{localidad.desc_localidad}
								</MenuItem>
							))}
						</Select>
						{fieldError('localidad_id') && <FormHelperText>{fieldError('localidad_id')}</FormHelperText>}
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={3} style={{ display: 'none' }}>
					<FormControl fullWidth error={!!fieldError('estado')}>
						<InputLabel id='estado-form-label' shrink>Estado *</InputLabel>
						<Select
							labelId='estado-form-label'
							label='Estado *'
							displayEmpty
							notched
							disabled={viewOnly || !editingId}
							value={form.estado}
							onChange={(e) => setForm({ ...form, estado: e.target.value })}
						>
							<MenuItem value=''>
								<em>Seleccione</em>
							</MenuItem>
							{estados.map((estado) => (
								<MenuItem key={estado.id} value={String(estado.id)}>
									{estado.label || estado.value}
								</MenuItem>
							))}
						</Select>
						{fieldError('estado') ? (
							<FormHelperText>{fieldError('estado')}</FormHelperText>
						) : (
							!editingId && <FormHelperText>Se asigna automáticamente como "Creado" al registrar el evento.</FormHelperText>
						)}
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						label='Lugar *'
						inputProps={{ maxLength: 300 }}
						value={form.lugar}
						onChange={(e) => setForm({ ...form, lugar: e.target.value })}
						error={!!fieldError('lugar')}
						helperText={fieldError('lugar')}
						disabled={viewOnly}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						label='Dirección (opcional)'
						inputProps={{ maxLength: 300 }}
						value={form.direccion}
						onChange={(e) => setForm({ ...form, direccion: e.target.value })}
						error={!!fieldError('direccion')}
						helperText={fieldError('direccion')}
						disabled={viewOnly}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormControl fullWidth>
						<InputLabel id='requiere-prerregistro-form-label'>¿Requiere prerregistro?</InputLabel>
						<Select
							labelId='requiere-prerregistro-form-label'
							label='¿Requiere prerregistro?'
							disabled={viewOnly}
							value={form.requiere_prerregistro}
							onChange={(e) => setForm({ ...form, requiere_prerregistro: e.target.value })}
						>
							<MenuItem value='SI'>Sí</MenuItem>
							<MenuItem value='NO'>No</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				{form.requiere_prerregistro === 'SI' && (
					<>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								type='date'
								label='Fecha inicio prerregistro *'
								InputLabelProps={{ shrink: true }}
								inputProps={{ min: getTodayISO() }}
								value={form.fecha_inicio_prerregistro}
								onChange={(e) => setForm({ ...form, fecha_inicio_prerregistro: e.target.value })}
								error={!!fieldError('fecha_inicio_prerregistro')}
								helperText={fieldError('fecha_inicio_prerregistro')}
								disabled={viewOnly}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								type='date'
								label='Fecha final prerregistro *'
								InputLabelProps={{ shrink: true }}
								value={form.fecha_fin_prerregistro}
								onChange={(e) => setForm({ ...form, fecha_fin_prerregistro: e.target.value })}
								error={!!fieldError('fecha_fin_prerregistro')}
								helperText={fieldError('fecha_fin_prerregistro')}
								disabled={viewOnly}
							/>
						</Grid>
						{editingId && (
							<Grid item xs={12} sm={6} md={3}>
								<TextField
									fullWidth
									type='number'
									label='Total de prerregistrados'
									value={form.conteo_prerregistrados}
									disabled
								/>
							</Grid>
						)}
						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth error={!!fieldError('requiere_notificacion_correo')}>
								<InputLabel id='notificacion-correo-form-label' shrink>¿Notificar por correo? *</InputLabel>
								<Select
									labelId='notificacion-correo-form-label'
									label='¿Notificar por correo? *'
									displayEmpty
									notched
									disabled={viewOnly}
									value={form.requiere_notificacion_correo}
									onChange={(e) => setForm({ ...form, requiere_notificacion_correo: e.target.value })}
								>
									<MenuItem value=''>
										<em>Seleccione</em>
									</MenuItem>
									<MenuItem value='SI'>Sí</MenuItem>
									<MenuItem value='NO'>No</MenuItem>
								</Select>
								{fieldError('requiere_notificacion_correo') && (
									<FormHelperText>{fieldError('requiere_notificacion_correo')}</FormHelperText>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth error={!!fieldError('franjas')}>
								<InputLabel id='franja-horaria-form-label' shrink>Franja horaria *</InputLabel>
								<Select
									labelId='franja-horaria-form-label'
									label='Franja horaria *'
									multiple
									displayEmpty
									notched
									disabled={viewOnly}
									value={form.franjas.map((franja) => franja.franja_horaria_id)}
									onChange={(e) => {
										const value = e.target.value;
										const seleccionadas = typeof value === 'string' ? value.split(',') : value;
										setForm({
											...form,
											franjas: seleccionadas.map((franjaHorariaId) => {
												const existente = form.franjas.find((franja) => franja.franja_horaria_id === franjaHorariaId);
												return existente || { franja_horaria_id: franjaHorariaId, cupo_maximo_prerregistro: '' };
											}),
										});
									}}
									renderValue={(selected) =>
										selected.length === 0 ? (
											<em>Seleccione</em>
										) : (
											<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
												{selected.map((value) => {
													const franja = franjasHorarias.find((f) => String(f.id) === value);
													return <Chip key={value} label={franja?.label || franja?.value || value} size='small' />;
												})}
											</Box>
										)
									}
								>
									{franjasHorarias.map((franja) => (
										<MenuItem key={franja.id} value={String(franja.id)}>
											<Checkbox checked={form.franjas.some((f) => f.franja_horaria_id === String(franja.id))} />
											<ListItemText primary={franja.label || franja.value} />
										</MenuItem>
									))}
								</Select>
								{fieldError('franjas') && <FormHelperText>{fieldError('franjas')}</FormHelperText>}
							</FormControl>
						</Grid>
						{form.franjas.length > 0 && (
							<Grid item xs={12}>
								<Table size='small'>
									<TableHead>
										<TableRow>
											<TableCell>Franja horaria</TableCell>
											<TableCell>Cupo máximo de prerregistro *</TableCell>
											{editingId && <TableCell>Cantidad de prerregistrados</TableCell>}
										</TableRow>
									</TableHead>
									<TableBody>
										{form.franjas.map((franja, index) => {
											const franjaCatalogo = franjasHorarias.find((f) => String(f.id) === franja.franja_horaria_id);
											return (
												<TableRow key={franja.franja_horaria_id}>
													<TableCell>{franjaCatalogo?.label || franjaCatalogo?.value || franja.franja_horaria_id}</TableCell>
													<TableCell>
														<TextField
															size='small'
															type='number'
															inputProps={{ min: 1 }}
															value={franja.cupo_maximo_prerregistro}
															onChange={(e) => {
																const franjas = [...form.franjas];
																franjas[index] = { ...franjas[index], cupo_maximo_prerregistro: e.target.value };
																setForm({ ...form, franjas });
															}}
															disabled={viewOnly}
														/>
													</TableCell>
													{editingId && (
														<TableCell>
															<TextField
																size='small'
																type='number'
																value={franja.conteo_prerregistrados ?? 0}
																disabled
															/>
														</TableCell>
													)}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</Grid>
						)}
					</>
				)}

				<Grid item xs={12}>
					<Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ pt: 2 }}>
						{viewOnly ? (
							<Button variant='contained' onClick={() => navigate(EVENTOS_LIST_PATH)}>
								Cerrar
							</Button>
						) : (
							<>
								<Button variant='outlined' color='error' onClick={() => navigate(EVENTOS_LIST_PATH)}>
									Cancelar
								</Button>
								<Button variant='contained' onClick={handleSubmitEvento}>
									{editingId ? 'Enviar' : 'Registrar'}
								</Button>
							</>
						)}
					</Stack>
				</Grid>
			</Grid>

			{editingId && isAdminRole() && (
				<Stack spacing={4} sx={{ mt: 4 }} divider={<Divider />}>
					<AsociacionVacantesPage embedded readOnly={viewOnly} />
					<AsociacionOrientacionesPage embedded readOnly={viewOnly} />
					<AsociacionFormacionesPage embedded readOnly={viewOnly} />
				</Stack>
			)}

			<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={open} onClose={() => setOpen(false)}>
				<Alert onClose={() => setOpen(false)} variant='filled' severity={alert ? 'success' : 'error'} sx={{ whiteSpace: 'pre-line' }}>
					{alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
					{alertMsg}
				</Alert>
			</Snackbar>
		</div> 
	);
};

const AdminEventoEmpleabilidadFormWrapper = ({ mode }: AdminEventoEmpleabilidadFormWrapperProps) => {
	const title = mode === 'view' ? 'Ver evento' : mode === 'edit' ? 'Editar evento' : 'Registrar evento';
	return (
		<>
			<PageTitle breadcrumbs={[]}>{title}</PageTitle>
			<EventoEmpleabilidadFormPage mode={mode} />
		</>
	);
};

export { AdminEventoEmpleabilidadFormWrapper };
